/**
 * Shared reading of a talent's questionnaire.
 *
 * The questionnaire is a free-form JSON map on `talents`, and every workspace
 * that shows a candidate needs the same three answers from it: which ESG areas
 * they actually know, how senior they are, and where they are. Deriving that in
 * each view drifted immediately — the triage board and the daily inbox
 * disagreed about what "strong in an area" meant — so the rules live here once.
 *
 * The area vocabulary and the experience ranges themselves now live one level
 * up, in `taxonomy/areas.ts` and `taxonomy/experience.ts`, because the employer
 * dashboard on the public site reads a candidate the same way and can import an
 * import-free module but not a panel view. They are re-exported below so every
 * view keeps importing from here.
 */

import {
    AREA_LABELS,
    LEVEL_LABELS,
    LEVEL_RANK,
    canonicalAreaKey,
    isInflated,
    readAreas,
    type AreaLevel,
    type TalentArea
} from "../taxonomy/areas";
import { experienceFloor, experienceLabel } from "../taxonomy/experience";

export { AREA_LABELS, LEVEL_LABELS, LEVEL_RANK, canonicalAreaKey };
export type { AreaLevel, TalentArea };

/** Chip colours track strength, so a board scans by colour before it reads. */
export const LEVEL_COLORS: Record<AreaLevel, string> = {
    none: "grayLighter",
    interested: "blueLighter",
    beginner: "cyanLighter",
    intermediate: "orangeLighter",
    advanced: "greenLighter"
};

export interface TalentSummary {
    areas: TalentArea[];
    /** Areas at intermediate or better — what makes someone worth contacting. */
    strongAreas: TalentArea[];
    /** Areas claimed at `advanced` — depth, as opposed to breadth. */
    advancedAreas: TalentArea[];
    /**
     * The candidate ticked advanced across half the vocabulary or more. Ranking
     * must not reward this: sorting purely by "most strong areas" puts the least
     * credible self-assessments at the top of the queue.
     */
    inflated: boolean;
    /** Ready to print, e.g. "7–10 años". Null when the question was skipped. */
    experienceLabel: string | null;
    /** Lower bound of the declared range, for ordering only. */
    experienceFloor: number | null;
    location: string | null;
    languages: string[];
    salaryIdeal: number | null;
    salaryCurrency: string | null;
    workModel: string[];
    completed: boolean;
}

function num(value: unknown): number | null {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

/**
 * Read a talent row into the shape every workspace renders.
 *
 * Defensive throughout: 100 of the 238 live rows never finished the
 * questionnaire, and the ones that did were written by several form versions —
 * `sustainability_areas` is a map today but arrives as an array on older rows,
 * and both appear in production.
 */
export function summarizeTalent(talent: any): TalentSummary {
    const q = (talent?.questionnaire ?? {}) as Record<string, any>;

    const { areas } = readAreas(q);

    const city = q.city ?? talent?.city ?? null;
    const country = q.country ?? talent?.country ?? null;
    const location = [city, country].filter(Boolean).join(", ") || talent?.location || null;

    const languages = Array.isArray(q.languages)
        ? q.languages
            .map((l: any) => (typeof l === "string" ? l : l?.language))
            .filter((l: any): l is string => typeof l === "string" && l.length > 0)
        : [];

    const workModel = Array.isArray(q.preferred_work_model)
        ? q.preferred_work_model.filter((m: any): m is string => typeof m === "string")
        : [];

    const advancedAreas = areas.filter(a => a.rank === LEVEL_RANK.advanced);

    return {
        areas,
        strongAreas: areas.filter(a => a.rank >= LEVEL_RANK.intermediate),
        advancedAreas,
        inflated: isInflated(areas),
        // Sustainability experience is the relevant number; total experience is
        // the fallback when only that was answered.
        experienceLabel:
            experienceLabel(q.years_experience_sustainability) ??
            experienceLabel(q.years_experience_total),
        experienceFloor:
            experienceFloor(q.years_experience_sustainability) ??
            experienceFloor(q.years_experience_total),
        location,
        languages,
        salaryIdeal: num(q.salary_ideal ?? q.salary_min),
        salaryCurrency: typeof q.salary_currency === "string" ? q.salary_currency : null,
        workModel,
        completed: Boolean(talent?.questionnaire_completed_at)
    };
}

/**
 * Order two candidates by how worth-a-look they are, best first.
 *
 * Depth before breadth, and an inflated profile sinks. Sorting by
 * `strongAreas.length` alone did the opposite: the five candidates at the top of
 * the queue were the five who ticked intermediate-or-better in all 17 areas,
 * which the matching engine separately flags as "perfil inflado". A finished
 * questionnaire breaks ties, because there is simply more to go on.
 */
export function compareTalentStrength(a: TalentSummary, b: TalentSummary): number {
    if (a.inflated !== b.inflated) return a.inflated ? 1 : -1;
    return (
        b.advancedAreas.length - a.advancedAreas.length ||
        b.strongAreas.length - a.strongAreas.length ||
        (b.experienceFloor ?? 0) - (a.experienceFloor ?? 0) ||
        Number(b.completed) - Number(a.completed)
    );
}

/** Every area key seen across a set of talents, for building a filter list. */
export function collectAreaKeys(talents: any[]): { key: string; label: string; count: number }[] {
    const counts = new Map<string, number>();
    for (const t of talents) {
        for (const area of summarizeTalent(t).areas) {
            counts.set(area.key, (counts.get(area.key) ?? 0) + 1);
        }
    }
    return [...counts.entries()]
        .map(([key, count]) => ({ key, label: AREA_LABELS[key] ?? key, count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

// ── Salary ──────────────────────────────────────────────────────────

/** Where a monthly salary figure came from. Mirrors `salary_unit_source`. */
export type SalarySource = "declared" | "inferred" | "unresolved";

export interface SalaryDisplay {
    /** The headline, already formatted: "mín. 20.833 · ideal 30.000 MXN/mes". */
    text: string;
    source: SalarySource;
    /** Set when the figures are not what the candidate typed — shown as a note. */
    note: string | null;
}

/**
 * How a salary answer should be shown to a recruiter.
 *
 * For most of the base the two numbers on file are not what this panel used to
 * print them as. The form said "anual" and offered `40000` as its example,
 * which in Mexico is a month, so half the answers are monthly and half yearly
 * and the column recorded neither. `backfill-salary-period.ts` writes a reading
 * of each row into the questionnaire, and this shows that reading — never the
 * bare number, which means two different things depending on who typed it.
 *
 * The provenance is on screen because it changes what the number is worth. A
 * declared figure is a fact about what someone will accept. An inferred one is
 * this system's best guess at what they meant, right about 96% of the time,
 * and a recruiter about to open a conversation on it deserves to know which
 * kind they are looking at. Where it could not be read at all, the raw answer
 * is printed with the unit called missing rather than quietly dropped — the
 * person did answer, and the failure is ours.
 */
export function describeSalary(q: Record<string, any> | null | undefined): SalaryDisplay | null {
    const values = q ?? {};
    const raw = { min: toNumber(values.salary_min), ideal: toNumber(values.salary_ideal) };
    if (raw.min === null && raw.ideal === null) return null;

    const currency = typeof values.salary_currency === "string" ? values.salary_currency.trim() : "";
    const source = readSource(values);

    if (source === "unresolved") {
        return {
            text: join(raw.min, raw.ideal, currency, ""),
            source,
            note: "No sabemos si es mensual o anual — se preguntó sin especificar la unidad."
        };
    }

    const monthly = monthlyFigures(values, raw, source);
    return {
        text: join(monthly.min, monthly.ideal, currency, "/mes"),
        source,
        note: source === "inferred"
            ? `Deducido de lo que escribió (${join(raw.min, raw.ideal, currency, "")}), no de una respuesta suya.`
            : null
    };
}

function readSource(values: Record<string, any>): SalarySource {
    const stored = values.salary_unit_source;
    if (stored === "declared" || stored === "inferred" || stored === "unresolved") return stored;
    // Saved since the last backfill run: the form records the period now, and
    // dividing by twelve is arithmetic rather than a judgement, so this does
    // not need the inference rule to show a declared answer correctly.
    if (values.salary_period === "mensual" || values.salary_period === "anual") return "declared";
    // An answer with no period and no stored reading has not been read yet.
    // Showing the bare number would be the original bug, so it reads as
    // unresolved until the backfill has been over it.
    return "unresolved";
}

function monthlyFigures(
    values: Record<string, any>,
    raw: { min: number | null; ideal: number | null },
    source: SalarySource
): { min: number | null; ideal: number | null } {
    const stored = { min: toNumber(values.salary_monthly_min), ideal: toNumber(values.salary_monthly_ideal) };
    if (stored.min !== null || stored.ideal !== null) return stored;
    if (source !== "declared") return { min: null, ideal: null };
    const perYear = values.salary_period === "anual";
    return {
        min: raw.min === null ? null : Math.round(perYear ? raw.min / 12 : raw.min),
        ideal: raw.ideal === null ? null : Math.round(perYear ? raw.ideal / 12 : raw.ideal)
    };
}

function toNumber(value: unknown): number | null {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

/** "mín. 20.833 · ideal 30.000 MXN/mes", skipping whichever half is missing. */
function join(min: number | null, ideal: number | null, currency: string, suffix: string): string {
    const parts = [
        min === null ? null : `mín. ${min.toLocaleString("es-MX")}`,
        ideal === null ? null : `ideal ${ideal.toLocaleString("es-MX")}`
    ].filter(Boolean);
    if (parts.length === 0) return "—";
    return parts.join(" · ") + (currency ? ` ${currency}${suffix}` : suffix);
}
